import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  SimpleGrid,
  Switch,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { useUpdateEventMutation } from '../../app/api';
import { useEventContext } from '../../contexts/EventContext';
import { STAT_KEYS, STAT_LABELS, StatKey } from '../../types/dto';

const StatsConfigurationPanel: React.FC = () => {
  const { currentEvent, setCurrentEvent } = useEventContext();
  const [updateEvent, { isLoading }] = useUpdateEventMutation();
  const toast = useToast();

  const initialEnabled = useMemo(() => {
    const stored = currentEvent?.config?.enabledStats;
    return stored && stored.length > 0
      ? new Set<StatKey>(stored)
      : new Set<StatKey>(STAT_KEYS);
  }, [currentEvent?.config?.enabledStats]);

  const [enabled, setEnabled] = useState<Set<StatKey>>(initialEnabled);

  const toggle = (key: StatKey) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleSave = () => {
    if (!currentEvent) return;
    const enabledStats = STAT_KEYS.filter((k) => enabled.has(k));
    updateEvent({
      id: currentEvent.id,
      event: {},
      config: {
        ...currentEvent.config,
        enabledStats,
      },
    })
      .unwrap()
      .then((updated) => {
        setCurrentEvent(
          {
            ...currentEvent,
            ...updated,
            config: { ...currentEvent.config, enabledStats },
          },
          false
        );
        toast({
          title: 'Saved',
          description: 'Stats configuration updated',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      })
      .catch(() => {
        toast({
          title: 'Error',
          description: 'Failed to update stats configuration',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      });
  };

  return (
    <VStack align="stretch" spacing={4} p={2}>
      <Text>
        Choose which stats are tracked for this event. Disabled stats are hidden
        from scoring and player views, and are excluded from CSV exports.
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
        {STAT_KEYS.map((key) => (
          <FormControl
            key={key}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <FormLabel htmlFor={`stat-${key}`} mb="0">
              {STAT_LABELS[key].long}{' '}
              <Text as="span" color="gray.500" fontSize="sm">
                ({STAT_LABELS[key].short})
              </Text>
            </FormLabel>
            <Switch
              id={`stat-${key}`}
              isChecked={enabled.has(key)}
              onChange={() => toggle(key)}
            />
          </FormControl>
        ))}
      </SimpleGrid>
      <HStack justify="flex-end">
        <Button
          colorScheme="blue"
          onClick={handleSave}
          isLoading={isLoading}
          isDisabled={!currentEvent}
        >
          Save
        </Button>
      </HStack>
    </VStack>
  );
};

export default StatsConfigurationPanel;
